<template>
	<form
		class="form-element"
		:class="{ 'form-element--has-buttons': hasButtons, 'form-element--is-loading': loading }"
		@submit.prevent="onSubmit"
	>
		<div class="form-element__content">
			<slot />
		</div>

		<div
			v-if="hasButtons"
			:class="{ 'form-element__buttons--has-offset': buttonsOffset }"
			class="form-element__buttons"
		>
			<div v-if="buttonsOffset" class="form-element__buttons-offset-wrapper">
				<slot name="buttons" />
			</div>

			<slot v-else name="buttons" />
		</div>
	</form>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { useHasSlot } from '@/composables';
import provideValidator from '@/mixins/validation/provideValidator';

export default defineComponent({
	mixins: [provideValidator()],

	props: {
		loading: {
			type: Boolean,
			default: false,
		},

		buttonsOffset: {
			type: Boolean,
		},
	},

	emits: ['invalid', 'submit'],

	setup(props, { slots }) {
		return {
			hasButtons: useHasSlot('buttons', slots),
		};
	},

	computed: {
		formElement(): HTMLFormElement {
			return this.$el as HTMLFormElement;
		},
	},

	methods: {
		onSubmit(): void {
			this.$emit(this.validate() ? 'submit' : 'invalid');
		},

		submit(): void {
			this.formElement.submit();
		},

		reset(): void {
			this.formElement.reset();
		},
	},
});
</script>

<style lang="scss">
.form-element {
	$self: &;

	&__buttons {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 8rem;

		.base-button {
			padding: 1.5rem 3.8rem;

			&.base-button--minimal {
				min-width: initial;
				padding: 1.5rem 0;
			}
		}

		&--has-offset {
			display: grid;
			grid-template-columns: 3fr 4fr;
			gap: 2.4rem 2.4rem;
		}
	}

	&__buttons-offset-wrapper {
		display: flex;
		grid-column-start: 2;
		align-items: center;
		justify-content: space-between;
	}

	&--is-loading {
		#{$self}__content {
			@include loading-state;
		}
	}
}
</style>
